import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { reactive, ref, computed, PropType, nextTick, watch } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'

import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import PolicySelect from '@/views/dashboard/_components/policy-select/policy-select.vue'
import { PolicyType } from '@/views/dashboard/_enum/policy-type'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import {
  TimeViewService,
  LogDataReq,
  LogData,
  formatNetLimitLogAction,
  formatNetLimitLogLimitType,
} from '@/views/dashboard/manage/internet-restriction/_components/time-view/time-view.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  FormSize,
  FormType,
  ObjectFormItem,
  DateType,
  ActionType,
} from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import dayjs from 'dayjs'
import { formatTimeStamp } from '@/utils/time'
import { useParseTime } from '@/hooks'
import { ServicePermissionService, PermissionFe } from '@/services/service-permission.service'
import { formatTableEmpty } from '@/utils/format'

type FilterBarKeys = 'strategy_id' | 'group_id'

export default /*#__PURE__*/_defineComponent({
  __name: 'domain-intercept-event-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  /** 侧栏嵌入：日期 / 策略 id（如从策略列表「触发次数」） */
  embedDefaults: {
    type: Object as PropType<{ dateRange?: [string, string]; strategyId?: string } | null>,
    default: null
  },
},
  emits: ['viewAll', 'rawData'],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n()
const props = __props

function initialStrategyId () {
  return props.embedDefaults?.strategyId
}

function resolveInitialDateRange(): [string, string] | undefined {
  const ed = props.embedDefaults?.dateRange
  if (ed?.length === 2) return ed
  if (props.viewMode === TableViewMode.full) {
    return [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]
  }
  return undefined
}

const emits = __emit

const injector = useReflectiveInjector()
const timeViewService = injector.get(TimeViewService)
const servicePermissionService = injector.get(ServicePermissionService)
const dataApi = timeViewService.getLogData.bind(timeViewService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.internet_url_limit)

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const total = computed(() => tableRef.value?.total || 0)

const initialDateRange = resolveInitialDateRange()
const [initialStartTime, initialEndTime] = useParseTime(initialDateRange || [])

const queryParams = reactive<Partial<LogDataReq> & { date?: [string, string] }>({
  strategy_id: initialStrategyId(),
  group_id: -1,
  key_word: '',
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  start_time: initialStartTime,
  end_time: initialEndTime,
  date: initialDateRange,
})

const filterFormColumns = computed<
  ObjectFormItem<LogDataReq, FilterBarKeys> & {
    date?: any
  }
>(() => {
  void locale.value
  return {
    strategy_id: {
      colOpt: { style: 'flex: 0 0 130px' },
      slot: true,
      label: t('domainInterceptEvent.triggerPolicy'),
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      events: {
        change: () => void refreshList(),
      },
    },
    group_id: {
      colOpt: { style:'flex: 0 0 150px' },
      slot: true,
      label: t('portManage.group'),
      outSearchFiledFlag: true,
      hiddenAllLabel: true,
      type: FormType.Select,
      events: {
        change: () => void refreshList(),
      },
    },
    date: {
      colOpt: { style: 'flex: 0 0 280px' },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      type: FormType.Date,
      dateType: DateType.DateRange,
      events: {
        change: (val) => {
          if (val) {
            const [start, end] = useParseTime(val)
            queryParams.start_time = start
            queryParams.end_time = end
          } else {
            queryParams.start_time = undefined
            queryParams.end_time = undefined
          }
          void refreshList()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('portManage.startTime'),
        'end-placeholder': t('portManage.endTime'),
        clearable: true,
      },
    },
  }
})

const isShowRawData = ref(false)
const currentRow = ref<any>({})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: t('portManage.operation'), width: 120, align: 'left', fixed: 'right' },
    actionButtons: (row: LogData) => [
      {
        name: t('portManage.dataSource'),
        type: 'rawData',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding:10px 16px' : undefined,
    path: 'list',
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'device_id',
    selection: false,
    showTip: false,
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      {
        label: t('domainInterceptEvent.interceptTime'),
        prop: 'intercept_time',
        minWidth: 170,
      },
      {
        label: t('domainInterceptEvent.triggerPolicy'),
        prop: 'strategy_name',
        minWidth: 120,
        showOverflow: true,
        formatter: (row: LogData, val: unknown, column: unknown) =>
          formatTableEmpty(row, column as any, val),
      },
      {
        label: t('domainInterceptEvent.limitType'),
        prop: 'limit_type',
        minWidth: 100,
        formatter: (_row: LogData, val: unknown) => formatNetLimitLogLimitType(t, val),
      },
      ...computerInfoCols(true, true, t),
      {
        label: t('domainInterceptEvent.interceptDomain'),
        prop: 'domain',
        minWidth: 150,
        showOverflow: true,
      },
      {
        label: t('domainInterceptEvent.hitAction'),
        prop: 'action',
        minWidth: 100,
        formatter: (_row: LogData, val: unknown) => formatNetLimitLogAction(t, _row.limit_type),
      },
    ],
  }
})

async function handleSearch(value: string) {
  await nextTick()
  queryParams.key_word = value
  void refreshList()
}

async function handleQuery(form: any) {
  Object.assign(queryParams, form)
  await refreshList()
}

function handleQueryChange () {
  void refreshList()
}

function handleReset() {
  queryParams.strategy_id = initialStrategyId()
  queryParams.key_word = ''
  queryParams.group_id = -1
  const range = resolveInitialDateRange()
  if (range?.length === 2) {
    const [s, e] = useParseTime(range)
    queryParams.date = range
    queryParams.start_time = s
    queryParams.end_time = e
  } else {
    queryParams.date = undefined
    queryParams.start_time = undefined
    queryParams.end_time = undefined
  }
  void refreshList()
}

async function updateGroupId(groupId: number) {
  // GroupTreeSelector 在初始化时会回传一次当前分组；若分组未变化则不重复请求列表。
  if (queryParams.group_id === groupId) return
  queryParams.group_id = groupId
  queryParams.page = 1
  await refreshList()
}

function handleAction({ type, rowItem }: { type: any; rowItem?: LogData }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem
    isShowRawData.value = true
  }
}

watch(
  () => props.embedDefaults,
  (ed) => {
    if (props.viewMode !== TableViewMode.full || !ed) return

    let changed = false
    if (ed.dateRange?.length === 2) {
      const [s, e] = useParseTime(ed.dateRange)
      queryParams.date = [ed.dateRange[0], ed.dateRange[1]]
      queryParams.start_time = s
      queryParams.end_time = e
      changed = true
    }
    if (ed.strategyId !== undefined && queryParams.strategy_id !== ed.strategyId) {
      queryParams.strategy_id = ed.strategyId || undefined
      changed = true
    }
    if (changed) {
      queryParams.page = 1
      void nextTick(() => void refreshList())
    }
  },
  { deep: true, immediate: true },
)

function download(type: 0 | 1) {
  const params: Partial<LogDataReq> = {
    group_id: queryParams.group_id,
    key_word: queryParams.key_word,
    start_time: queryParams.start_time,
    end_time: queryParams.end_time,
    strategy_id: queryParams.strategy_id,
  }
  timeViewService.download(type, params, t('portManage.exportDomainIntercept'))
}

__expose({
  tableRef,
  getList: () => void refreshList(),
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      "has-permission": _unref(hasPermission),
      api: _unref(dataApi),
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
      pageOption: {
      style: 'padding-bottom: 16px',
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        (props.viewMode !== _unref(TableViewMode).preview)
          ? (_openBlock(), _createBlock(SearchFilterBar, {
              key: 0,
              modelValue: queryParams,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
              "form-columns": filterFormColumns.value,
              disabled: !_unref(hasPermission),
              "show-search": true,
              "search-placeholder": _ctx.$t('domainInterceptEvent.searchPlaceholder'),
              "popover-threshold": 2,
              onSearch: handleSearch,
              onQuery: handleQuery,
              onReset: handleReset
            }, {
              strategy_id: _withCtx(() => [
                _createVNode(PolicySelect, {
                  width: "100%",
                  size: "small",
                  disabled: !_unref(hasPermission),
                  modelValue: queryParams.strategy_id,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams.strategy_id) = $event)),
                  "strategy-type": _unref(PolicyType).internet,
                  onChange: handleQueryChange
                }, null, 8, ["disabled", "modelValue", "strategy-type"])
              ]),
              group_id: _withCtx(() => [
                _createVNode(GroupTreeSelector, {
                  width: "100%",
                  disabled: !_unref(hasPermission),
                  "terminal-type": [_unref(TerminalType).windows, _unref(TerminalType).windowsServer],
                  onSelected: updateGroupId
                }, null, 8, ["disabled", "terminal-type"])
              ]),
              actions: _withCtx(() => [
                _createVNode(ExportDropdown, {
                  "has-permission": _unref(hasPermission),
                  onExport: download
                }, null, 8, ["has-permission"])
              ]),
              _: 1
            }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"]))
          : _createCommentVNode("", true)
      ]),
      ip_address: _withCtx(({ row }) => [
        _createVNode(IPColumn, {
          cip: row.client_ip,
          sip: row.system_ip
        }, null, 8, ["cip", "sip"])
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "has-permission", "api", "modelValue"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})